import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, Subject } from 'rxjs';
import { MyConfigOrganization } from '../models/myConfig';
import { MyConfigService } from './my-config.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  jwtHelperService: JwtHelperService;
  private token: string;
  private isLoggedIn: boolean;
  private role: string;
  public username: string;
  public userId: string;
  public hasMandatoryUnsignedContracts: string;
  public displayName: string;
  private userTimedOutSubject: Subject<string> = new Subject<string>();
  private userTimedOutObservable: Observable<string>;
  private soonTimedOutSubject: Subject<boolean> = new Subject<boolean>();
  private soonTimedOutObservable: Observable<boolean>;
  public minBeforeTimeOut = 8;
  private userLoginSubject: Subject<boolean> = new Subject<boolean>();
  private userLoginObservable: Observable<boolean>;

  constructor(public router: Router, private myConfigService: MyConfigService) {
    this.jwtHelperService = new JwtHelperService();
    this.userTimedOutObservable = this.userTimedOutSubject.asObservable();
    this.soonTimedOutObservable = this.soonTimedOutSubject.asObservable();
    this.userLoginObservable = this.userLoginSubject.asObservable();

    this.token = this.getToken();
    if (this.isTokenValid()) {
      this.setTokenValues(this.token);

      if (localStorage.getItem('MyConfig') != null) {
        this.myConfigService.setMyConfig(JSON.parse(localStorage.getItem('MyConfig')));
      }
    }

    setInterval(() => {
      this.checkUserTimedOut();
      this.checkAboutToGetTimedOut();
    }, 5000);
  }

  private checkUserTimedOut() {
    if (this.token && this.jwtHelperService.isTokenExpired(this.token)) {
      const exp = this.jwtHelperService.getTokenExpirationDate(this.token);
      const maxDate = new Date(exp.getTime() + 3000);
      console.log(`warning: , exp: ${exp}, maxDate: ${maxDate}`);
      if (new Date().getTime() >= maxDate.getTime()) {
        // console.log('checkUserTimedOut()');
        this.userTimedOutSubject.next(this.username);
        this.clearTokenValues();
      }
    }
  }

  public forceTimeOut() {
    // console.log('forceTimeOut()');
    this.removeContractsList();
    this.userTimedOutSubject.next(this.username);
    this.clearTokenValues();
  }

  public getUserTimedOutObservable() {
    return this.userTimedOutObservable;
  }

  private checkAboutToGetTimedOut() {
    if (this.token) {
      const exp = this.jwtHelperService.getTokenExpirationDate(this.token);
      // show notification before 8 mins of expiry
      const notificationTime = new Date(exp.getTime() - this.minBeforeTimeOut * 1000 * 60);
      if (
        new Date().getTime() >= notificationTime.getTime() &&
        !this.jwtHelperService.isTokenExpired(this.token)
      ) {
        this.soonTimedOutSubject.next(true);
      } else {
        this.soonTimedOutSubject.next(false);
      }
    } else {
      this.soonTimedOutSubject.next(false);
    }
  }

  public forceTimeOutWarning() {
    this.soonTimedOutSubject.next(true);
  }

  public getUserAboutToGetTimedOutObservable() {
    return this.soonTimedOutObservable;
  }

  public getUserLoginObservable() {
    return this.userLoginObservable;
  }

  private clearTokenValues() {
    this.isLoggedIn = false;
    this.token = null;
    this.role = null;
    this.username = null;
    this.userId = null;
    this.hasMandatoryUnsignedContracts = null;
    localStorage.removeItem('id_token');
    this.removeContractsList();
    this.myConfigService.removeMyConfig();
    this.clearShuttleCheckinSession();
  }

  public clearShuttleCheckinSession(): void {
    try {
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('shuttle_checkin_bus_selected')) {
          sessionStorage.removeItem(key);
        }
      });
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('shuttle_checkin_bus_selected')) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      // ignore storage access errors
    }
  }

  public setToken(token) {
    this.token = token;
    localStorage.setItem('id_token', token);
    this.clearShuttleCheckinSession();
    this.setTokenValues(token);
  }

  public setContractsList(contracts) {
    localStorage.setItem('ContractsList', JSON.stringify(contracts));
  }

  public removeContractsList() {
    if (localStorage.getItem('ContractsList') != null) {
      localStorage.removeItem('ContractsList');
    }
  }

  public refreshToken() {
    console.log('AuthService - refreshToken()');
  }

  private isTokenValid() {
    // console.log('isTokenValid', this.token);
    // console.log('isTokenExpired', this.jwtHelperService.isTokenExpired(this.token));
    // console.log('getTokenExpirationDate', this.jwtHelperService.getTokenExpirationDate(this.token));
    // console.log('decodeToken', this.jwtHelperService.decodeToken(this.token));
    if (this.token) {
      return !this.jwtHelperService.isTokenExpired(this.token);
    }
    return false;
  }

  private setTokenValues(token) {
    if (token) {
      const decodedToken = this.jwtHelperService.decodeToken(token);
      this.isLoggedIn = true;
      this.role = decodedToken.role;
      this.userId = decodedToken.sub;
      this.displayName = decodedToken.displayname;
      this.username = decodedToken.username;
      this.hasMandatoryUnsignedContracts = decodedToken.hasMandatoryUnsignedContracts;
      this.setAppInsightsAuthId(decodedToken.sub);
      this.userLoginSubject.next(true);
    }
  }

  private setAppInsightsAuthId(id: string) {
    const validatedId = id.replace(/[,;=| ]+/g, '_');
    // appInsights.setAuthenticatedUserContext(validatedId);
  }

  logout(): void {
    this.token = null;
    this.role = null;
    this.displayName = null;
    this.userId = null;
    this.removeContractsList();
    this.clearTokenValues();
    this.clearShuttleCheckinSession();
    this.soonTimedOutSubject.next(false);
    this.userLoginSubject.next(false);
  }

  loggedIn() {
    return this.isLoggedIn && !this.isTokenExpired();
  }

  private isTokenExpired(): boolean {
    const payloadBase64Url = this.token.split('.')[1];
    const payloadJson = atob(payloadBase64Url); // Decode from base64 to JSON
    const payload = JSON.parse(payloadJson);

    if (!payload.exp) {
      throw new Error("Token does not have an expiration date.");
    }

    const expirationTime = payload.exp * 1000;
    return Date.now() >= expirationTime;
  }

  getToken(): string {
    if (localStorage.getItem('id_token') != null) {
      if (this.jwtHelperService.isTokenExpired(localStorage.getItem('id_token'))) {
        this.clearTokenValues();
        return null;
      }
    }
    return localStorage.getItem('id_token');
  }

  getRoleName() {
    return this.role;
  }

  getFullUserName() {
    return this.displayName;
  }

  getUserEmail() {
    return this.username;
  }

  getHasMandatoryUnsignedContracts() {
    return this.hasMandatoryUnsignedContracts;
  }

  getUserId() {
    return this.userId;
  }

  getTokenExpirationDate() {
    return this.jwtHelperService.getTokenExpirationDate(this.getToken());
  }

  isAdmin() {
    if (this.loggedIn() && this.getRoleName() && this.getRoleName().indexOf('Administrator') > -1) {
      return true;
    } else {
      return false;
    }
  }

  isSiteAdmin() {
    if (this.loggedIn() && this.getRoleName() && this.getRoleName().indexOf('SiteAdmin') > -1) {
      return true;
    } else {
      return false;
    }
  }

  isCompanyAdmin(inOrganization: string): boolean {
    return this.myConfigService.hasOrganizationRole(inOrganization, 'CompanyAdmin');
  }

  isConnectStaff(inOrganization: string): boolean {
    return this.myConfigService.hasOrganizationRole(inOrganization, 'Staff');
  }

  isQualityHotelStaff(inOrganization: string): boolean {
    return this.myConfigService.hasOrganizationRole(inOrganization, 'Staff');
  }

  isDriver(inOrganization: string): boolean {
    return this.myConfigService.hasOrganizationRole(inOrganization, 'Driver');
  }

  isStaff(inOrganization: string): boolean {
    return this.myConfigService.hasOrganizationRole(inOrganization, 'Staff');
  }

  isDashboardUser(inOrganization: string): boolean {
    return this.myConfigService.hasOrganizationRole(inOrganization, 'Dashboard');
  }

  isAdminOrSiteAdmin() {
    var result = false;
    if (this.getRoleName() != null) {

      if ((this.loggedIn() && this.getRoleName().indexOf('Administrator') > -1) || this.getRoleName().indexOf('SiteAdmin') > -1) {
        return true;
      }
    }
    return result;
  }
}
